/**
 * ADS Shipping Rules (AU) — single source of truth
 * ------------------------------------------------------------------
 * Pure data + decision engine, no DOM. Both cart-location-gate.js and
 * cart-checkout-gate.js call window.ADSShipping.resolve(state, zip, facts).
 *
 * Front-end responsibility is DISPLAY + GATING only: it shows the free
 * shipping threshold / fee, blocks checkout for non-deliverable carts,
 * and excludes "no free shipping" items from the progress bar. The real
 * freight is computed by Shopify's own shipping settings at checkout —
 * keep the numbers below in sync with the admin shipping rates.
 *
 * Facts (computed server-side in snippets/cart-shipping-facts.liquid,
 * because /cart.js does not expose product tags):
 *   {
 *     hasRawMeat, hasPickupOnlySku, hasHeavyOrLitter, hasAuspostTag,
 *     totalWeightGrams, heavyLineTotal (cents),
 *     pickupSkuItems:[{title,sku}], rawMeatItems:[...], heavyItems:[...]
 *   }
 *
 * resolve() returns:
 *   {
 *     blocked, blockedItems:[title], pickupGuide, // 不配送: grey out + banner
 *     noFree, noFreeItems:[title], noFreeAmount(cents), // 不包邮: exclude from bar
 *     mode: 'threshold'|'weight'|'auspost'|'blocked',
 *     threshold(dollars)|null, fee(dollars)|null, feeLabel, message
 *   }
 */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ */
  /*  Postcode zone parser                                               */
  /*  "3000,3002-3006,3008" -> { singles:Set, ranges:[[3002,3006]] }      */
  /* ------------------------------------------------------------------ */
  function parseZone(str) {
    var singles = {};
    var ranges = [];
    str.split(",").forEach(function (tok) {
      tok = tok.trim();
      if (!tok) return;
      if (tok.indexOf("-") > -1) {
        var p = tok.split("-");
        ranges.push([parseInt(p[0], 10), parseInt(p[1], 10)]);
      } else {
        singles[parseInt(tok, 10)] = true;
      }
    });
    return { singles: singles, ranges: ranges };
  }

  function inZone(z, zone) {
    if (!zone || isNaN(z)) return false;
    if (zone.singles[z]) return true;
    for (var i = 0; i < zone.ranges.length; i++) {
      if (z >= zone.ranges[i][0] && z <= zone.ranges[i][1]) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ */
  /*  VIC raw-meat delivery tiers (transcribed verbatim from spec)        */
  /* ------------------------------------------------------------------ */

  // $99 free / else $15 — "ADS Select VIC Metro"
  var VIC_T99 = parseZone(
    "3000,3001,3002,3003,3005,3006,3008,3010,3011,3012,3013,3015,3016,3018,3019,3020,3021,3022,3023,3024,3025,3026,3027,3028,3029,3030,3031,3032,3033,3034,3036,3037,3038,3039,3040,3041,3042,3043,3044,3045,3046,3047,3048,3049,3050,3051,3052,3053,3054,3055,3056,3057,3058,3059,3060,3061,3062,3063,3064,3065,3066,3067,3068,3070,3071,3072,3073,3074,3075,3076,3078,3079,3081,3082,3083,3084,3085,3086,3087,3088,3089,3090,3091,3093,3094,3095,3105,3106,3107,3110,3113,3114,3115,3116,3134,3135,3136,3137,3140,3152,3153,3155,3156,3158,3164,3175,3176,3177,3178,3179,3180,3195,3196,3197,3198,3199,3200,3201,3207,3752,3802,3803,3804,3805,3806,3807,3809,3810,3975,3977,3976,3978,3910,3911,3912,3913,3930,3931,3933,3160,3754,3755,3756,3758,3764"
  );

  // $79 free / else $15 — "ADS Local Delivery"
  var VIC_T79 = parseZone(
    "3004,3101,3102,3103,3104,3108,3109,3111,3121,3122,3123,3124,3125,3126,3127,3128,3129,3130,3131,3132,3133,3141,3142,3143,3144,3145,3146,3147,3148,3149,3150,3151,3161,3162,3163,3165,3166,3167,3168,3169,3170,3171,3172,3173,3174,3181,3182,3183,3184,3185,3186,3187,3188,3189,3190,3191,3192,3193,3194,3202,3204,3205,3206,3800"
  );

  // $120 free / else $20  (5760 assumed typo for 3760 — pending confirm)
  var VIC_T120 = parseZone(
    "3064,3432,3433,3434,3435,3444,3446,3447,3448,3450,3451,3453,3461,3462,3463,3515,3522,3523,3550,3551,3552,3555,3556,3750,3751,3753,3424,3428,3429,3430,3335,3336,3337,3338,3340,3341,3342,3345,3350,3351,3352,3355,3356,3363,3458,3460,3437,3438,3440,3442,3431,3228,3230,3231,3232,3812,3813,3814,3815,3816,3818,3820,3821,3822,3823,3824,3825,3831,3835,3840,3842,3844,3847,3850,3851,3854,3857,3858,3860,3862,3869,3870,3871,3922,3925,3945,3950,3953,3956,3959,3960,3962,3980,3981,3984,3990,3995,3996,3096,3097,3099,3139,3154,3159,3760,3761,3765,3766,3767,3770,3775,3777,3781,3782,3783,3785,3786,3787,3788,3789,3791,3792,3793,3795,3796,3797,3799,3806,3807,3808,3714,3715,3717,3719,3720,3722,3723,3757,3763,3778,3779,3212,3214,3215,3216,3217,3218,3219,3220,3222,3223,3224,3225,3226,3227,3910,3911,3912,3913,3915,3916,3918,3919,3920,3926,3927,3928,3929,3934,3936,3937,3938,3939,3940,3941,3942,3943,3944"
  );

  // $160 free / else $30
  var VIC_T160 = parseZone(
    "3564,3610,3614,3616,3617,3618,3620,3629,3630,3631,3633,3634,3635,3636,3644,3730,3658,3659,3660,3662,3607,3608,3621"
  );

  // Explicit conflict overrides (spec, "以维州配送为优先"): zip -> tier key.
  var VIC_OVERRIDE = {
    3910: "t99", 3911: "t99", 3912: "t99", 3913: "t99", 3158: "t99",
    3806: "t120", 3807: "t120", 3808: "t120", 3761: "t120",
  };

  var VIC_TIERS = {
    t79: { freeOver: 79, fee: 15 },
    t99: { freeOver: 99, fee: 15 },
    t120: { freeOver: 120, fee: 20 },
    t160: { freeOver: 160, fee: 30 },
  };

  /** Return the VIC raw-meat tier for a postcode, or null if out of range. */
  function vicTier(z) {
    if (isNaN(z)) return null;
    if (VIC_OVERRIDE[z]) return VIC_TIERS[VIC_OVERRIDE[z]];
    // Non-conflicting postcodes are unique to one list; check all four.
    if (inZone(z, VIC_T79)) return VIC_TIERS.t79;
    if (inZone(z, VIC_T99)) return VIC_TIERS.t99;
    if (inZone(z, VIC_T120)) return VIC_TIERS.t120;
    if (inZone(z, VIC_T160)) return VIC_TIERS.t160;
    return null;
  }

  /* ------------------------------------------------------------------ */
  /*  Mail zones ($69 free / else $10 unless noted)                       */
  /* ------------------------------------------------------------------ */

  // VIC small-order mail (no raw meat). $69 free / else $10.
  var VIC_SMALL = parseZone(
    "3000-3220,3335-3341,3350,3353-3356,3427-3443,3689-3690,3750-3811,3910-3920,3926-3944,3975-3978,3980-3983,8000-8999,9999"
  );

  // NSW / ACT / SA mail. $69 free / else $10. (3939 dropped: VIC postcode.)
  var NSW_ACT_SA = parseZone(
    "0200,2000,2006-2012,2015-2050,2052,2060-2077,2079-2082,2084-2097,2099-2122,2125-2128,2130-2148,2150-2156,2158,2160-2168,2170-2179,2190-2200,2203-2214,2216-2234,2280-2287,2289-2300,2302-2308,2500,2502,2505,2506,2522,2525-2530,2600-2609,2611,2612,2614-2618,2620,2640,2641,2745,2747,2749,2750,2753-2756,2759-2763,2765-2770,2773,2774,2776-2780,2782-2785,2900,2902-2906,2911-2914,5000,5005-5025,5031-5035,5037-5052,5061-5070,5072-5076,5081-5098,5106-5116,5118,5121,5125-5127,5152,5154,5155,5158-5173,5242,5245,5250,5251,5950"
  );

  // QLD mail zone (weight-based fee, see qldFee).
  var QLD_ZONE = parseZone(
    "4000-4014,4017-4022,4029-4032,4034-4037,4051,4053-4055,4059-4061,4064-4070,4072-4078,4101-4125,4127-4133,4151-4161,4163-4165,4169-4174,4178,4179,4205,4207-4230,4272,4280,4285,4300,4301,4303-4305,4500-4506,4556-4558,4572,4573,4575"
  );

  /* ------------------------------------------------------------------ */
  /*  State detection (from postcode)                                     */
  /* ------------------------------------------------------------------ */
  function detectState(zip) {
    var n = parseInt(zip, 10);
    if (isNaN(n)) return null;
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return "VIC";
    if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return "NSW";
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return "ACT";
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return "QLD";
    if (n >= 5000 && n <= 5999) return "SA";
    if ((n >= 6000 && n <= 6797) || (n >= 6800 && n <= 6999)) return "WA";
    if (n >= 7000 && n <= 7999) return "TAS";
    if (n >= 800 && n <= 999) return "NT";
    return null;
  }

  function isValidPostcode(zip) {
    return /^\d{3,4}$/.test((zip || "").trim()) && detectState(zip) !== null;
  }

  /* ------------------------------------------------------------------ */
  /*  QLD weight-based fee                                                */
  /* ------------------------------------------------------------------ */
  function qldFee(kg) {
    // <=9kg: $69 free / else $10 (handled as threshold by caller)
    // 9.01-15kg: flat $10
    // >15kg: $10 + $1 per kg over 15 (round up)
    if (kg <= 15) return 10;
    return 10 + Math.ceil(kg - 15);
  }

  /* ------------------------------------------------------------------ */
  /*  Result builders                                                     */
  /* ------------------------------------------------------------------ */
  function titles(arr) {
    return (arr || []).map(function (i) { return i.title || i.sku; });
  }

  function blocked(items, message, pickupGuide) {
    return {
      blocked: true,
      blockedItems: items,
      pickupGuide: !!pickupGuide,
      noFree: false, noFreeItems: [], noFreeAmount: 0,
      mode: "blocked",
      threshold: null, fee: null, feeLabel: "",
      message: message || "",
    };
  }

  function base() {
    return {
      blocked: false, blockedItems: [], pickupGuide: false,
      noFree: false, noFreeItems: [], noFreeAmount: 0,
      mode: "threshold", threshold: null, fee: null, feeLabel: "", message: "",
    };
  }

  /** Attach VIC "no free shipping" (Heavy / cat litter) exclusion. */
  function withHeavy(res, f) {
    if (f.hasHeavyOrLitter) {
      res.noFree = true;
      res.noFreeItems = titles(f.heavyItems);
      res.noFreeAmount = f.heavyLineTotal || 0;
    }
    return res;
  }

  /** AUSPOST national fallback. */
  function auspost(f) {
    var r = base();
    var kg = (f.totalWeightGrams || 0) / 1000;
    if (f.hasAuspostTag || kg <= 5) {
      r.mode = "auspost";
      r.threshold = 69;
      r.fee = 12;
      r.feeLabel = "Australia Post";
      return r;
    }
    // >5kg, no AUSPOST tag: real cost, excluded from free shipping.
    r.mode = "auspost";
    r.threshold = null;
    r.fee = null;
    r.noFree = true;
    r.noFreeItems = [];
    r.message = "Shipping is calculated by parcel weight at checkout.";
    return r;
  }

  /* ------------------------------------------------------------------ */
  /*  Main resolver                                                       */
  /* ------------------------------------------------------------------ */
  function resolve(state, zip, f) {
    f = f || {};
    var z = parseInt(zip, 10);
    var kg = (f.totalWeightGrams || 0) / 1000;

    // Pickup-only SKUs: never deliverable anywhere -> block, guide to pickup.
    if (f.hasPickupOnlySku) {
      return blocked(
        titles(f.pickupSkuItems),
        "The following items are Click & Collect only.",
        true
      );
    }

    if (state === "VIC") {
      if (f.hasRawMeat) {
        var tier = vicTier(z);
        if (!tier) {
          return blocked(
            titles(f.rawMeatItems),
            "We can't deliver fresh/frozen items to this postcode. Please choose store pickup.",
            true
          );
        }
        var r = base();
        r.threshold = tier.freeOver;
        r.fee = tier.fee;
        r.feeLabel = "VIC delivery";
        return withHeavy(r, f);
      }
      if (inZone(z, VIC_SMALL)) {
        var r2 = base();
        r2.threshold = 69;
        r2.fee = 10;
        r2.feeLabel = "Courier";
        return withHeavy(r2, f);
      }
      // VIC postcode outside small-order zone, no raw meat -> AUSPOST fallback.
      return withHeavy(auspost(f), f);
    }

    if (state === "NSW" || state === "ACT" || state === "SA") {
      if (f.hasRawMeat || f.hasHeavyOrLitter) {
        return blocked(
          titles(f.rawMeatItems).concat(titles(f.heavyItems)),
          "The following items can only be delivered within VIC. Please remove them to continue.",
          false
        );
      }
      if (inZone(z, NSW_ACT_SA)) {
        var r3 = base();
        r3.threshold = 69;
        r3.fee = 10;
        r3.feeLabel = "Standard shipping";
        return r3;
      }
      return auspost(f);
    }

    if (state === "QLD") {
      if (f.hasRawMeat || f.hasHeavyOrLitter) {
        return blocked(
          titles(f.rawMeatItems).concat(titles(f.heavyItems)),
          "The following items can only be delivered within VIC. Please remove them to continue.",
          false
        );
      }
      if (inZone(z, QLD_ZONE)) {
        var r4 = base();
        if (kg <= 9) {
          r4.mode = "threshold";
          r4.threshold = 69;
          r4.fee = 10;
        } else {
          // Over 9kg: no fixed QLD rate to advertise. Real freight is
          // charged by parcel weight at checkout — don't show a fee here
          // and don't fall back to the Australia Post rate.
          r4.mode = "weight";
          r4.threshold = null; // no free-shipping tier above 9kg
          r4.fee = null;
          r4.message = "Shipping is calculated by parcel weight at checkout.";
        }
        r4.feeLabel = "QLD shipping";
        return r4;
      }
      return auspost(f);
    }

    // WA / TAS / NT or any postcode not covered above -> AUSPOST fallback.
    if (f.hasRawMeat || f.hasHeavyOrLitter) {
      return blocked(
        titles(f.rawMeatItems).concat(titles(f.heavyItems)),
        "The following items can only be delivered within VIC. Please remove them to continue.",
        false
      );
    }
    return auspost(f);
  }

  window.ADSShipping = {
    resolve: resolve,
    detectState: detectState,
    isValidPostcode: isValidPostcode,
    vicTier: vicTier,
  };
})();
